import {
  HHButton,
  HHGrid,
  HHSelect,
  HHSelectChangeEvent,
} from '@hinge-health/react-component-library';
import { IconButton, TextField, Typography } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ActionJsonSchema,
  JsonSchemaProperty,
} from '../../hooks/use-supported-actions';
import { AceEditorComponent } from '../ace-editor-component';

export type KvPair = { _id: string; key: string; value: string };
export type TaggedItem = { _id: string; value: string };
export const generateId = (): string =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
type FieldValue =
  | string
  | number
  | boolean
  | TaggedItem[]
  | KvPair[]
  | Record<string, unknown>;

const isKvPairsType = (prop: JsonSchemaProperty): boolean =>
  prop.type === 'object' && !!prop.additionalProperties;

const isJsonEditorType = (prop: JsonSchemaProperty): boolean =>
  (prop.type === 'object' && !prop.properties && !prop.additionalProperties) ||
  (prop.type === 'array' && prop.items?.type === 'object');

const isValidPattern = (val: string, pattern: string): boolean => {
  try {
    return new RegExp(pattern).test(val);
  } catch {
    return false;
  }
};

const isValidNumeric = (
  val: string | number,
  prop: JsonSchemaProperty,
): boolean => {
  const num = typeof val === 'number' ? val : Number(val);

  if (isNaN(num)) {
    return false;
  }

  if (prop.minimum !== undefined && num < prop.minimum) {
    return false;
  }

  if (prop.maximum !== undefined && num > prop.maximum) {
    return false;
  }

  return true;
};

const isValidTaggedArray = (
  val: TaggedItem[],
  prop: JsonSchemaProperty,
): boolean => {
  // Intentionally defaults to minItems=1 for required array fields (deviates from JSON
  // Schema semantics where omitted minItems means 0). Required arrays must have at least
  // one non-empty entry for a valid submission.
  if (val.length < (prop.minItems ?? 1)) {
    return false;
  }

  return val.every(item => item.value !== '');
};

const isValidKvPairs = (val: KvPair[]): boolean => {
  if (!Array.isArray(val) || val.length === 0) {
    return false;
  }

  return val.every(pair => pair.key !== '');
};

const isValidJson = (val: string): boolean => {
  try {
    JSON.parse(val);
    return true;
  } catch {
    return false;
  }
};

const isValidNestedObject = (
  val: Record<string, unknown>,
  prop: JsonSchemaProperty,
): boolean => {
  if (!prop.required) {
    return true;
  }

  return prop.required.every(subField => {
    const subVal = val[subField];
    return subVal !== undefined && subVal !== null && subVal !== '';
  });
};

const isNestedObjectType = (prop: JsonSchemaProperty): boolean =>
  prop.type === 'object' && !!prop.properties && !prop.additionalProperties;

const resolveJsonValue = (
  currentValue: FieldValue,
  exampleValue: unknown,
): string => {
  if (typeof currentValue === 'string') {
    return currentValue;
  }

  if (currentValue !== undefined) {
    return JSON.stringify(currentValue, null, 2);
  }

  if (exampleValue !== undefined) {
    return JSON.stringify(exampleValue, null, 2);
  }

  return '';
};

export type FieldOverrideRenderer = (
  value: unknown,
  onChange: (value: unknown) => void,
) => JSX.Element;

interface TagArrayFieldProps {
  label: string;
  items: TaggedItem[];
  onChange: (items: TaggedItem[]) => void;
  renderItem: (
    item: TaggedItem,
    index: number,
    onItemChange: (value: string) => void,
  ) => JSX.Element;
  addTestId: string;
  itemLabel?: string;
}

export const TagArrayField = ({
  label,
  items,
  onChange,
  renderItem,
  addTestId,
  itemLabel = 'item',
}: TagArrayFieldProps): JSX.Element => (
  <div>
    <Typography variant="body2" gutterBottom>
      {label}
    </Typography>
    {items.map((item, index) => (
      <HHGrid
        container
        spacing={1}
        key={item._id}
        alignItems="center"
        sx={{ mb: 1 }}
      >
        <HHGrid item xs={10}>
          {renderItem(item, index, (value): void => {
            onChange(
              items.map(i => (i._id === item._id ? { ...i, value } : i)),
            );
          })}
        </HHGrid>
        <HHGrid item xs={2}>
          <IconButton
            size="small"
            onClick={(): void =>
              onChange(items.filter(i => i._id !== item._id))
            }
            aria-label={`Remove ${itemLabel} ${index + 1}`}
          >
            ✕
          </IconButton>
        </HHGrid>
      </HHGrid>
    ))}
    <HHButton
      hhVariant="variant-bypass"
      variant="text"
      data-testid={addTestId}
      onClick={(): void =>
        onChange([...items, { _id: generateId(), value: '' }])
      }
    >
      + Add Item
    </HHButton>
  </div>
);

const buildMetadata = (
  schema: ActionJsonSchema,
  data: Record<string, FieldValue>,
): Record<string, unknown> => {
  const result: Record<string, unknown> = {};

  Object.entries(data).forEach(([key, val]) => {
    const prop = schema.properties[key];

    if (!prop) {
      return;
    }

    if (isKvPairsType(prop) && Array.isArray(val)) {
      const obj: Record<string, string> = {};
      (val as KvPair[]).forEach(pair => {
        if (pair.key) {
          obj[pair.key] = pair.value;
        }
      });
      result[key] = obj;
    } else if (
      prop.type === 'array' &&
      prop.items?.type === 'string' &&
      Array.isArray(val)
    ) {
      result[key] = (val as TaggedItem[]).map(item => item.value);
    } else if (isJsonEditorType(prop) && typeof val === 'string') {
      try {
        result[key] = JSON.parse(val);
      } catch {
        // skip invalid JSON
      }
    } else if (prop.type === 'number' || prop.type === 'integer') {
      result[key] = Number(val);
    } else {
      result[key] = val;
    }
  });

  return result;
};

const computeIsValid = (
  schema: ActionJsonSchema,
  data: Record<string, FieldValue>,
): boolean => {
  const required = schema.required ?? [];

  return required.every(field => {
    const val = data[field];
    const prop = schema.properties[field];

    if (!prop) {
      return true;
    }

    if (val === undefined || val === null || val === '') {
      return false;
    }

    if (prop.pattern && typeof val === 'string') {
      return isValidPattern(val, prop.pattern);
    }

    if (
      (prop.type === 'number' || prop.type === 'integer') &&
      (typeof val === 'string' || typeof val === 'number')
    ) {
      return isValidNumeric(val, prop);
    }

    if (
      prop.type === 'array' &&
      prop.items?.type === 'string' &&
      Array.isArray(val)
    ) {
      return isValidTaggedArray(val as TaggedItem[], prop);
    }

    if (isKvPairsType(prop)) {
      return isValidKvPairs(val as KvPair[]);
    }

    if (isJsonEditorType(prop) && typeof val === 'string') {
      return isValidJson(val);
    }

    if (prop.type === 'object' && prop.properties && prop.required) {
      return isValidNestedObject(val as Record<string, unknown>, prop);
    }

    return true;
  });
};

interface DynamicActionFormProps {
  schema: ActionJsonSchema;
  example: Record<string, unknown>;
  onChange: (data: object) => void;
  onIsValid: (isValid: boolean) => void;
  fieldOverrides?: Record<string, FieldOverrideRenderer>;
}

export const DynamicActionForm = ({
  schema,
  example,
  onChange,
  onIsValid,
  fieldOverrides,
}: DynamicActionFormProps): JSX.Element | null => {
  const [formData, setFormData] = useState<Record<string, FieldValue>>({});

  // Stable refs so the schema-reset effect and handleFieldChange never
  // re-run due to inline callbacks from the parent
  const onChangeRef = useRef(onChange);
  const onIsValidRef = useRef(onIsValid);
  const formDataRef = useRef(formData);
  onChangeRef.current = onChange;
  onIsValidRef.current = onIsValid;
  formDataRef.current = formData;

  const handleFieldChange = useCallback(
    (fieldName: string, value: FieldValue): void => {
      const updated = { ...formDataRef.current, [fieldName]: value };
      setFormData(updated);
      onChangeRef.current(buildMetadata(schema, updated));
      onIsValidRef.current(computeIsValid(schema, updated));
    },
    [schema],
  );

  useEffect(() => {
    const hasRequiredFields = (schema.required?.length ?? 0) > 0;
    onIsValidRef.current(!hasRequiredFields);
    onChangeRef.current({});
    setFormData({});
  }, [schema]);

  const renderSubField = (
    fieldName: string,
    property: JsonSchemaProperty,
    isRequired: boolean,
    value: unknown,
    onSubChange: (value: unknown) => void,
  ): JSX.Element => {
    const label = `${fieldName}${isRequired ? ' *' : ''}`;
    const stringValue =
      value !== undefined && value !== null ? String(value) : '';

    if (property.type === 'string' && property.enum) {
      const selectOptions = property.enum.map(v => ({
        value: v,
        displayName: v,
      }));
      return (
        <HHSelect
          fullWidth
          selectOptions={selectOptions}
          initialSelectValue={stringValue}
          label={label}
          onChange={(e: HHSelectChangeEvent): void =>
            onSubChange(e.target.value as string)
          }
        />
      );
    }

    return (
      <TextField
        fullWidth
        label={label}
        type={
          property.type === 'number' || property.type === 'integer'
            ? 'number'
            : 'text'
        }
        value={stringValue}
        inputProps={{
          min: property.minimum,
          max: property.maximum,
          name: fieldName,
          role:
            property.type === 'number' || property.type === 'integer'
              ? 'spinbutton'
              : 'textbox',
        }}
        onChange={(e): void =>
          onSubChange(
            property.type === 'number' || property.type === 'integer'
              ? Number(e.target.value)
              : e.target.value,
          )
        }
        variant="outlined"
      />
    );
  };

  const renderField = (
    fieldName: string,
    property: JsonSchemaProperty,
    isRequired: boolean,
  ): JSX.Element => {
    try {
      const label = `${fieldName}${isRequired ? ' *' : ''}`;
      const exampleValue = example[fieldName];
      const placeholder =
        exampleValue !== undefined ? String(exampleValue) : undefined;
      const currentValue = formData[fieldName];

      if (fieldOverrides?.[fieldName]) {
        return fieldOverrides[fieldName](currentValue, (value: unknown): void =>
          handleFieldChange(fieldName, value as FieldValue),
        );
      }

      if (property.type === 'string' && property.enum) {
        const selectOptions = property.enum.map(v => ({
          value: v,
          displayName: v,
        }));
        return (
          <HHSelect
            fullWidth
            selectOptions={selectOptions}
            initialSelectValue={(currentValue as string) ?? ''}
            label={label}
            onChange={(e: HHSelectChangeEvent): void =>
              handleFieldChange(fieldName, e.target.value as string)
            }
          />
        );
      }

      if (property.type === 'string') {
        return (
          <TextField
            fullWidth
            label={label}
            value={(currentValue as string) ?? ''}
            placeholder={placeholder}
            onChange={(e): void => handleFieldChange(fieldName, e.target.value)}
            variant="outlined"
            inputProps={{ name: fieldName, role: 'textbox' }}
          />
        );
      }

      if (property.type === 'number' || property.type === 'integer') {
        return (
          <TextField
            fullWidth
            type="number"
            label={label}
            value={(currentValue as string) ?? ''}
            placeholder={placeholder}
            inputProps={{
              min: property.minimum,
              max: property.maximum,
              name: fieldName,
              role: 'spinbutton',
            }}
            onChange={(e): void => handleFieldChange(fieldName, e.target.value)}
            variant="outlined"
          />
        );
      }

      if (property.type === 'array' && property.items?.type === 'string') {
        const items = (currentValue as TaggedItem[]) ?? [];
        return (
          <TagArrayField
            label={label}
            items={items}
            onChange={(newItems): void =>
              handleFieldChange(fieldName, newItems)
            }
            renderItem={(item, index, onItemChange): JSX.Element => (
              <TextField
                fullWidth
                value={item.value}
                placeholder={placeholder}
                onChange={(e): void => onItemChange(e.target.value)}
                variant="outlined"
                size="small"
                inputProps={{
                  'aria-label': `${fieldName} item ${index + 1}`,
                  name: `${fieldName}[${index}]`,
                  role: 'textbox',
                }}
              />
            )}
            addTestId={`dynamic-form-${fieldName}-add-item-button`}
            itemLabel={`${fieldName} item`}
          />
        );
      }

      if (isNestedObjectType(property)) {
        const nestedValue = (currentValue as Record<string, unknown>) ?? {};
        const subRequired = property.required ?? [];
        return (
          <div>
            <Typography variant="body2" gutterBottom>
              {label}
            </Typography>
            <HHGrid
              container
              spacing={2}
              sx={{
                pl: 2,
                borderLeft: theme => `2px solid ${theme.palette.divider}`,
              }}
            >
              {Object.entries(property.properties ?? {}).map(
                ([subName, subProp]) => (
                  <HHGrid item xs={12} key={subName}>
                    {renderSubField(
                      subName,
                      subProp,
                      subRequired.includes(subName),
                      nestedValue[subName],
                      (subValue): void => {
                        handleFieldChange(fieldName, {
                          ...nestedValue,
                          [subName]: subValue,
                        });
                      },
                    )}
                  </HHGrid>
                ),
              )}
            </HHGrid>
          </div>
        );
      }

      if (isKvPairsType(property)) {
        const pairs = (currentValue as KvPair[]) ?? [];
        return (
          <div>
            <Typography variant="body2" gutterBottom>
              {label}
            </Typography>
            {pairs.map((pair, index) => (
              <HHGrid
                container
                spacing={1}
                key={pair._id}
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <HHGrid item xs={5}>
                  <TextField
                    fullWidth
                    value={pair.key}
                    placeholder="key"
                    onChange={(e): void => {
                      handleFieldChange(
                        fieldName,
                        pairs.map(p =>
                          p._id === pair._id
                            ? { ...p, key: e.target.value }
                            : p,
                        ),
                      );
                    }}
                    variant="outlined"
                    size="small"
                    inputProps={{
                      'aria-label': `${fieldName} key ${index + 1}`,
                      name: `${fieldName}_key_${index}`,
                      role: 'textbox',
                    }}
                  />
                </HHGrid>
                <HHGrid item xs={5}>
                  <TextField
                    fullWidth
                    value={pair.value}
                    placeholder="value"
                    onChange={(e): void => {
                      handleFieldChange(
                        fieldName,
                        pairs.map(p =>
                          p._id === pair._id
                            ? { ...p, value: e.target.value }
                            : p,
                        ),
                      );
                    }}
                    variant="outlined"
                    size="small"
                    inputProps={{
                      'aria-label': `${fieldName} value ${index + 1}`,
                      name: `${fieldName}_value_${index}`,
                      role: 'textbox',
                    }}
                  />
                </HHGrid>
                <HHGrid item xs={2}>
                  <IconButton
                    size="small"
                    onClick={(): void => {
                      handleFieldChange(
                        fieldName,
                        pairs.filter(p => p._id !== pair._id),
                      );
                    }}
                    aria-label={`Remove ${fieldName} entry ${index + 1}`}
                  >
                    ✕
                  </IconButton>
                </HHGrid>
              </HHGrid>
            ))}
            <HHButton
              hhVariant="variant-bypass"
              variant="text"
              data-testid={`dynamic-form-${fieldName}-add-entry-button`}
              onClick={(): void =>
                handleFieldChange(fieldName, [
                  ...pairs,
                  { _id: generateId(), key: '', value: '' },
                ])
              }
            >
              + Add Entry
            </HHButton>
          </div>
        );
      }

      const jsonValue = resolveJsonValue(currentValue, exampleValue);

      return (
        <AceEditorComponent
          label={label}
          value={jsonValue}
          helperText=""
          onChange={(value): void => handleFieldChange(fieldName, value)}
          dataTestId={`${fieldName}-editor`}
        />
      );
    } catch {
      return (
        <Typography variant="body2" color="error">
          Failed to render field &quot;{fieldName}&quot;
        </Typography>
      );
    }
  };

  if (!schema.properties || Object.keys(schema.properties).length === 0) {
    return null;
  }

  const requiredFields = schema.required ?? [];

  return (
    <HHGrid container spacing={2}>
      {Object.entries(schema.properties).map(([fieldName, property]) => (
        <HHGrid item xs={12} key={fieldName}>
          {renderField(fieldName, property, requiredFields.includes(fieldName))}
        </HHGrid>
      ))}
    </HHGrid>
  );
};
